import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SideNavLayout from "../customsidenav/sidenavlayout";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import React, { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import { Backdrop, CircularProgress, styled } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AptCard from "./modals/aptcard";
import { leftarrow, rightarrow } from "../colorcoding/svg/iconsvg";
import ModalSwitch from "./modals/modalswitch";
import ProvidersStatus from "./modals/providersstatus";
import NewAppointments from "./modals/newappointment";
import showmore from "../../src/assets/Schedule/showmore.svg";
import api from "../api/api";
import calendericon from "../assets/Schedule/schedularcalender.svg";
import { customObjectToDateString } from "../dateutils/date";
import isoWeek from "dayjs/plugin/isoWeek";
import { getPracticeId, practiceShortName } from "../config/practice";

import { CalendarPanel } from "../components/calendar/CalendarPanel";
dayjs.extend(isoWeek);

const CustomDateCalendar = styled(DateCalendar)(() => ({
  /* Force calendar width */
  width: "280px",
  maxWidth: "280px",

  "&.MuiDateCalendar-root": {
    padding: 0,
    height: "auto",
  },

  "& .MuiPickersCalendarHeader-root": {
    display: "none",
  },

  "& .MuiDayCalendar-monthContainer": {
    marginTop: "4px",
    paddingBottom: "0px",
  },

  "& .MuiDayCalendar-weekDayLabel": {
    fontSize: "12px",
    color: "#6B7280",
  },

  "& .MuiPickersDay-root": {
    margin: "2px",
    width: "36px",
    height: "36px",
    fontSize: "13px",
  },

  "& .MuiPickersDay-root.Mui-selected": {
    backgroundColor: "#1D2951",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#1D2951",
    },
  },
}));

const CalendarHeader = ({ value, onChange, setViewType }) => {
  const handleToday = () => {
    setViewType("daily");
    onChange(dayjs());
  };

  const handleWeek = () => {
    setViewType("weekly");
    onChange(dayjs().startOf("week"));
  };

  const handleMonth = () => {
    setViewType("monthly");
    onChange(dayjs().startOf("month"));
  };

  return (
    <div className="w-full mb-3">
      {/* Top actions */}
      <div className="flex justify-between items-center mb-2">
        <div className="flex gap-2">
          {["Today", "This Week", "Month"].map((label) => (
            <button
              key={label}
              onClick={
                label === "Today"
                  ? handleToday
                  : label === "This Week"
                  ? handleWeek
                  : handleMonth
              }
              className="px-3 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Month navigation */}
      <div className="flex items-center justify-between px-2">
        <span className="font-semibold text-sm">
          {value.format("MMMM YYYY")}
        </span>

        <div className="flex gap-2">
          <button
            onClick={() => onChange(value.subtract(1, "month"))}
            className="text-lg"
          >
            ‹
          </button>
          <button
            onClick={() => onChange(value.add(1, "month"))}
            className="text-lg"
          >
            ›
          </button>
        </div>
      </div>
    </div>
  );
};

const FilterChip = ({ label, onRemove }) => (
  <div
    className="flex items-center gap-2 px-2 py-[2px] text-xs text-indigo-600"
    style={{
      borderRadius: "4px",
      border: "1px solid #818CF8",
      background: "#EEF2FF",
    }}
  >
    <span className="whitespace-nowrap">{label}</span>
    <button
      onClick={onRemove}
      className="text-indigo-600 hover:text-indigo-800 text-sm leading-none"
    >
      ✕
    </button>
  </div>
);

const Schedule = () => {
  const weekLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const [selectedDate, setSelectedDate] = React.useState(dayjs());
  const [providerStatus, setproviderStatus] = useState(false);
  const [isAptCard, setIsAptCard] = useState(false);
  const [Appointments, setNewAppointments] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("All Providers");
  const [selectedAppointmentType, setSelectedAppointmentType] = useState("");
  const [scheduleData, setScheduleData] = React.useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [appointmentCardopen, setIsappointmentCardOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const practiseid = getPracticeId();
  const [providerOptions, setProviderOptions] = useState([]);
  const calendarRef = useRef(null);
  const suggestionref = useRef(null);
  const [screen, setScreen] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [isfutureappointment, setIsFutureAppointment] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [viewType, setViewType] = useState("daily"); // daily | weekly | monthly

  let debounceTimer;

  const isToday = selectedDate.isSame(dayjs(), "day");

  const getDateRange = () => {
    let start;
    let end;

    if (viewType === "daily") {
      start = selectedDate.startOf("day");
      end = null;
    }

    if (viewType === "weekly") {
      start = selectedDate.startOf("week");
      end = selectedDate.endOf("week");
    }

    if (viewType === "monthly") {
      start = selectedDate.startOf("month");
      end = selectedDate.endOf("month");
    }

    return {
      startdate: start.format("YYYY-MM-DD"),
      enddate: end ? end.format("YYYY-MM-DD") : undefined,
    };
  };

  const handleSearchChange = async (e) => {
    const searchQuery = e.target.value.toLowerCase();
    if (searchQuery === "") {
      setFilteredSuggestions([]);
      return;
    }

    clearTimeout(debounceTimer);

    debounceTimer = setTimeout(async () => {
      try {
        const response = await api.post(
          `/v1/${practiseid}/autocomplete/dssearch`,
          {
            searchtxt: searchQuery,
          }
        );
        if (response.status == 200) {
          setFilteredSuggestions(response?.data || []);
        }
      } catch (error) {
        console.error("Search API error:", error);
      }
    }, 1000);
  };

  const handleExpandToggle = (key) => {
    setExpandedRows((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const dateChipLabel = useMemo(() => {
    if (viewType === "daily") {
      if (isToday) return null;
      return selectedDate.format("MMM D, YYYY");
    }

    if (viewType === "weekly") {
      const start = selectedDate.startOf("week");
      const end = selectedDate.endOf("week");
      return `${start.format("MMM D")} – ${end.format("MMM D")}`;
    }

    if (viewType === "monthly") {
      return selectedDate.format("MMMM YYYY");
    }

    return null;
  }, [viewType, selectedDate, isToday]);

  const fetchScheduleData = async () => {
    setIsLoading(true);
    try {
      const { startdate, enddate } = getDateRange();

      const payload = {
        startdate,
        ...(enddate && { enddate }),
      };

      const response = await api.post(
        `/v1/${practiseid}/scheduler/schedule_list`,
        payload
      );

      if (response.status === 200) {
        setScheduleData(response.data);
      } else {
        setScheduleData([]);
      }
    } catch (error) {
      console.error("Error fetching schedule data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = `${practiceShortName()} - Scheduler`;
    fetchScheduleData();
  }, [selectedDate, viewType]);

  const handleViewChange = (type) => {
    setViewType(type);

    if (type === "daily") {
      setSelectedDate(dayjs());
    }

    if (type === "weekly") {
      setSelectedDate(dayjs().startOf("week"));
    }

    if (type === "monthly") {
      setSelectedDate(dayjs().startOf("month"));
    }
  };

  const handleProviderChange = (value) => {
    setSelectedProvider(value);
  };

  const handleAppointmentTypeChange = (value) => {
    setSelectedAppointmentType(value);
  };

  const handleDateChange = (prop) => {
    setIsCalendarOpen(false);
    setSelectedDate(prop);
  };

  const getAppointmentColorClass = (type) => {
    switch (type) {
      case "FOLLOW UP":
        return "#A8E6A1";
      case "FOLLOW UP MCAR":
        return "#FF6F61";
      case "3A SCAN":
        return "#00ff7f";
      case "ESTIM":
        return "#808080";
      case "CIMT":
        return "#ffa500";
      case "ALLERGY TEST":
        return "#cc00cc";
      case "CCA":
        return "#ca1f7b";
      case "BIAT":
        return "#e18e96";
      default:
        return "#ECECEE";
    }
  };

  const providerNames = scheduleData
    ? [...new Set(scheduleData.map((appointment) => appointment.providername))]
    : [];

const allProviders = selectedProvider === "All Providers"
  ? providerOptions.map(p => p.providername)
  : [selectedProvider];

  const filteredAppointments = useMemo(() => {
    if (!Array.isArray(scheduleData)) {
      console.log("scheduleData is not an array:", scheduleData);
      return [];
    }

    const result = scheduleData.filter(
      (appointment) =>
        appointment.visit_confirmation !== "Cancelled" && 
        (selectedProvider === "All Providers" ||
          appointment.providername === selectedProvider) &&
        (selectedAppointmentType === "" ||
          appointment.type === selectedAppointmentType)
    );

    console.log("Filtered appointments:", result);
    return result;
  }, [scheduleData, selectedProvider, selectedAppointmentType]);

  const timeSlots = [
    "8:00 AM",
    "9:00 AM",
    "10:00 AM",
    "11:00 AM",
    "12:00 PM",
    "1:00 PM",
    "2:00 PM",
    "3:00 PM",
    "4:00 PM",
    "5:00 PM",
    "6:00 PM",
    "7:00 PM",
  ];

  const formatTimeSlot = (time) => {
    const hour = parseInt(time.split(":")[0], 10);

    const slot = timeSlots.find((slot) => {
      let slotHour = parseInt(slot.split(":")[0], 10);
      let slotHour24 = slotHour;

      if (slot.includes("PM") && slotHour !== 12) slotHour24 += 12;
      if (slot.includes("AM") && slotHour === 12) slotHour24 = 0;

      return slotHour24 === hour;
    });

    return slot || dayjs(time, "HH:mm:ss").format("h:mm A");
  };

  // const groupedAppointments = filteredAppointments
  //   ? providerNames.reduce((acc, provider) => {
  //       const providerAppointments = filteredAppointments.filter(
  //         (appointment) => appointment.providername === provider
  //       );
  //       providerAppointments.forEach((appointment) => {
  //         const startTime = formatTimeSlot(appointment.starttime);
  //         if (!acc[startTime]) acc[startTime] = {};
  //         if (!acc[startTime][provider]) acc[startTime][provider] = [];
  //         acc[startTime][provider].push(appointment);
  //       });
  //       return acc;
  //     }, {})
  //   : {};
const groupedAppointments = filteredAppointments
  ? providerNames.reduce((acc, provider) => {
      const providerAppointments = filteredAppointments.filter(
        (appointment) => appointment.providername === provider
      );

      providerAppointments.forEach((appointment) => {
        const startTime = formatTimeSlot(appointment.starttime);

        if (!acc[startTime]) acc[startTime] = {};
        if (!acc[startTime][provider]) acc[startTime][provider] = [];

        // 🔥 dedupe by patient + hour + provider
        const exists = acc[startTime][provider].some(
          (a) =>
            a.patientid === appointment.patientid &&
            formatTimeSlot(a.starttime) === startTime
        );

        if (!exists) {
          acc[startTime][provider].push(appointment);
        }
      });

      return acc;
    }, {})
  : {};

  const appointmentTypes = Array.isArray(scheduleData)
    ? [...new Set(scheduleData.map((appointment) => appointment.type))]
    : [];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target)) {
        setIsCalendarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionref.current && !suggestionref.current.contains(e.target)) {
        setFilteredSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchProviderData = async () => {
    try {
      const response = await api.get(`/v1/${practiseid}/providers`);
      if (response.status === 200) {
        setProviderOptions(response.data);
      } else {
        setProviderOptions([]);
      }
    } catch (error) {
      console.error(error);
      setProviderOptions([]);
    } finally {
    }
  };

  useEffect(() => {
    fetchProviderData();
  }, []);

  const handleSuggestionClick = async (appointment) => {
    console.log(appointment,'app')
const filteredAppointments = scheduleData.filter((app) => {
  return String(app.patientid) === String(appointment?.patientid);
});

    
console.log(filteredAppointments,'fil')
    if (filteredAppointments?.length) {
      setIsFutureAppointment(false);
      setSelectedAppointment(filteredAppointments[0]);
      setSearchTerm(
        `${filteredAppointments[0]?.firstname} ${filteredAppointments[0]?.lastname}`
      );
      setFilteredSuggestions([]);
      setScreen(1);
      setIsAptCard(true);
    } else {
      const appointmentsdata = await api.get(
        `/v1/${practiseid}/appointments/${appointment?.patientid}/future`
      );
      if (appointmentsdata.status == 200) {
        setIsFutureAppointment(true);
        setSelectedAppointment(appointmentsdata?.data[0] || {});
        setSearchTerm(
          `${appointmentsdata?.data[0]?.firstname} ${appointmentsdata?.data[0]?.lastname}`
        );
        setFilteredSuggestions([]);
        setScreen(0);
        setIsAptCard(true);
      }
    }
  };

  const weekDays = useMemo(() => {
    const startOfWeek = selectedDate.startOf("week");

    return Array.from({ length: 7 }).map((_, index) => {
      const date = startOfWeek.add(index, "day");
      return {
        label: date.format("ddd"),
        date: date.format("DD"),
        fullDate: date.format("YYYY-MM-DD"),
      };
    });
  }, [selectedDate, viewType]);

  // const weeklyGroupedAppointments = useMemo(() => {
  //   if (viewType !== "weekly") return {};

  //   const grouped = {};

  //   filteredAppointments.forEach((appointment) => {
  //     const day = dayjs(appointment.startdate).format("YYYY-MM-DD");
  //     const time = formatTimeSlot(appointment.starttime);

  //     if (!grouped[time]) grouped[time] = {};
  //     if (!grouped[time][day]) grouped[time][day] = [];

  //     grouped[time][day].push(appointment);
  //   });

  //   return grouped;
  // }, [filteredAppointments, viewType]);
const weeklyGroupedAppointments = useMemo(() => {
  if (viewType !== "weekly") return {};

  const grouped = {};

  filteredAppointments.forEach((appointment) => {
    const day = dayjs(appointment.startdate).format("YYYY-MM-DD");
    const time = formatTimeSlot(appointment.starttime);

    if (!grouped[time]) grouped[time] = {};
    if (!grouped[time][day]) grouped[time][day] = [];

    // 🔥 dedupe by patient + hour + day
    const exists = grouped[time][day].some(
      (a) =>
        a.patientid === appointment.patientid &&
        formatTimeSlot(a.starttime) === time
    );

    if (!exists) {
      grouped[time][day].push(appointment);
    }
  });

  return grouped;
}, [filteredAppointments, viewType]);

  const weeklyHeaderStats = useMemo(() => {
    if (viewType !== "weekly") return {};

    const stats = {};

    filteredAppointments.forEach((appt) => {
      const dayKey = dayjs(appt.startdate).format("YYYY-MM-DD");
      const hour = parseInt(appt.starttime.split(":")[0], 10);

      if (!stats[dayKey]) {
        stats[dayKey] = { total: 0, am: 0, pm: 0 };
      }

      stats[dayKey].total += 1;
      hour < 12 ? stats[dayKey].am++ : stats[dayKey].pm++;
    });

    return stats;
  }, [filteredAppointments, viewType]);

  const monthDays = useMemo(() => {
    const start = selectedDate.startOf("month").startOf("isoWeek");
    const end = selectedDate.endOf("month").endOf("isoWeek");

    const days = [];
    let current = start;

    while (current.isBefore(end) || current.isSame(end, "day")) {
      days.push(current);
      current = current.add(1, "day");
    }

    return days;
  }, [selectedDate]);
const monthlyGroupedAppointments = useMemo(() => {
  if (viewType !== "monthly") return {};

  const grouped = {};

  filteredAppointments.forEach((appt) => {
    const dayKey = dayjs(appt.startdate).format("YYYY-MM-DD");
    const time = formatTimeSlot(appt.starttime);

    if (!grouped[dayKey]) grouped[dayKey] = [];

    // 🔥 dedupe by patient + hour
    const exists = grouped[dayKey].some(
      (a) =>
        a.patientid === appt.patientid &&
        formatTimeSlot(a.starttime) === time
    );

    if (!exists) {
      grouped[dayKey].push(appt);
    }
  });

  return grouped;
}, [filteredAppointments, viewType]);

  // const monthlyGroupedAppointments = useMemo(() => {
  //   if (viewType !== "monthly") return {};

  //   const grouped = {};

  //   filteredAppointments.forEach((appt) => {
  //     const dayKey = dayjs(appt.startdate).format("YYYY-MM-DD");
  //     if (!grouped[dayKey]) grouped[dayKey] = [];
  //     grouped[dayKey].push(appt);
  //   });

  //   return grouped;
  // }, [filteredAppointments, viewType]);

  const handlePrev = (e) => {
    e.stopPropagation();

    setSelectedDate((prev) => {
      if (viewType === "daily") return prev.subtract(1, "day");
      if (viewType === "weekly") return prev.subtract(1, "week");
      if (viewType === "monthly") return prev.subtract(1, "month");
      return prev;
    });
  };

  const handleNext = (e) => {
    e.stopPropagation();

    setSelectedDate((prev) => {
      if (viewType === "daily") return prev.add(1, "day");
      if (viewType === "weekly") return prev.add(1, "week");
      if (viewType === "monthly") return prev.add(1, "month");
      return prev;
    });
  };

  const openDailyFromMonthly = (dateString) => {
    setViewType("daily");
    setSelectedDate(dayjs(dateString));
  };

  return (
    <>
      <div className="h-full">
        <SideNavLayout>
          <div className="w-full mt-2 space-y-2">
            {/* ROW 1 */}
            <div className="flex w-full justify-between items-center">
              {/* LEFT: Calendar */}
              <div className="relative inline-block">
                <div
                  onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                  className="flex items-center justify-between px-2 py-1 bg-white rounded-lg shadow-sm cursor-pointer max-w-[180px]"
                >
                  <div className="flex flex-col leading-none">
                    <span className="text-[10px] text-gray-400">Today</span>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={handlePrev}
                        className="text-sm mb-2 text-gray-500 leading-none"
                      >
                        {leftarrow}
                      </button>

                      <span className="text-[12px] font-medium">
                        {dayjs(selectedDate).format("MM-DD-YYYY")}
                      </span>
                      <img
                        src={calendericon}
                        alt="Calendar"
                        className="w-3.5 h-3.5 opacity-60"
                      />
                      <button
                        onClick={handleNext}
                        className="text-sm mb-2 text-gray-500 leading-none"
                      >
                        {rightarrow}
                      </button>
                    </div>
                  </div>
                </div>

                {/* FILTER CHIPS */}
                <div className="flex gap-2 mt-2 flex-wrap">
                  {dateChipLabel && (
                    <FilterChip
                      label={dateChipLabel}
                      onRemove={() => {
                        setViewType("daily");
                        setSelectedDate(dayjs());
                      }}
                    />
                  )}

                  {selectedProvider !== "All Providers" && (
                    <FilterChip
                      label={selectedProvider}
                      onRemove={() => setSelectedProvider("All Providers")}
                    />
                  )}

                  {selectedAppointmentType && (
                    <FilterChip
                      label={selectedAppointmentType}
                      onRemove={() => setSelectedAppointmentType("")}
                    />
                  )}
                </div>

                {isCalendarOpen && (
                  <div
                    ref={calendarRef}
                    className="absolute left-0 top-full mt-2 z-50 flex flex-col gap-2"
                  >
                    {/* Quick views, as before: they set the view and the date. */}
                    <div className="bmcal-quick">
                      {[
                        ["Today", "daily", () => dayjs()],
                        ["This Week", "weekly", () => dayjs().startOf("week")],
                        ["Month", "monthly", () => dayjs().startOf("month")],
                      ].map(([label, view, date]) => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => {
                            setViewType(view);
                            setSelectedDate(date());
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                    <CalendarPanel value={selectedDate} onChange={handleDateChange} />
                  </div>
                )}
              </div>

              {/* RIGHT: View switch + filters */}
              <div className="flex items-center gap-3">
                {/* View switch */}
                <div className="bg-gray-100 rounded-[10px] p-1 inline-flex gap-1">
                  {["daily", "weekly", "monthly"].map((type) => (
                    <button
                      key={type}
                      onClick={() => handleViewChange(type)}
                      className={`px-3 py-1 text-sm rounded-[8px] transition ${
                        viewType === type
                          ? "bg-white shadow-md font-medium text-gray-900"
                          : "text-gray-600"
                      }`}
                    >
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </button>
                  ))}
                </div>

                {/* Provider */}
              <select
  value={selectedProvider}
  onChange={(e) => handleProviderChange(e.target.value)}
  className="px-3 py-2 border border-gray-300 rounded-md text-xs"
>
  <option value="All Providers">All Providers</option>

  {providerOptions.map((provider) => (
    <option key={provider.providerid} value={provider.providername}>
      {provider.providername}
    </option>
  ))}
</select>


                {/* Appointment type */}
                <select
                  value={selectedAppointmentType}
                  onChange={(e) => handleAppointmentTypeChange(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-xs"
                >
                  <option value="">All Appointments</option>
                  {appointmentTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* ROW 2 (Right aligned only) */}
            <div className="flex w-full justify-end items-center gap-4">
              {/* Search */}
              <div className="relative w-full max-w-md">
                <div className="flex items-center bg-[#F5F6FA] border border-[#D5D5D5] rounded-[15px]">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="text-gray-400 ml-3"
                  />
                  <input
                    type="text"
                    className="bg-[#F5F6FA] flex-grow py-1 pl-2 pr-3 text-base outline-none rounded-[15px]"
                    placeholder="Search"
                    onChange={handleSearchChange}
                  />
                </div>

                {filteredSuggestions.length > 0 && (
                  <div
                    ref={suggestionref}
                    className="absolute top-8 mt-1 bg-white shadow-lg rounded-lg z-10 max-h-[200px] overflow-y-auto"
                  >
                    {filteredSuggestions
                      .filter(
                        (value, index, self) =>
                          index ===
                          self.findIndex(
                            (t) => t.patientid === value.patientid
                          )
                      )
                      .map((appointment) => (
                        <div
                          key={appointment.patientid}
                          className="px-4 py-2 cursor-pointer hover:bg-gray-200"
                          onClick={() => handleSuggestionClick(appointment)}
                        >
                          {appointment?.patientinfo}
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* + icon */}
              <img
                src={showmore}
                className="w-8 cursor-pointer"
                onClick={() => setNewAppointments(true)}
                alt="+"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-4 flex-wrap">
            <div className="w-[80vw] flex-grow p-2 bg-white shadow-md rounded-lg h-auto overflow-y-auto custom-scrollbar">
              <div className="w-full overflow-x-auto overflow-y-visible custom-scrollbar-x">
                {viewType !== "monthly" && (
                  <table className="w-max min-w-[1200px] bg-white text-center table-fixed">
                    <thead className="sticky top-0 bg-white">
                      <tr>
                        <th className="py-2 px-4 border-b border-gray-200 text-[#2A2A2A] font-semibold">
                          {/* Empty header */}
                        </th>

                        {/* WEEKLY HEADER */}
                        {viewType === "weekly" ? (
                          weekDays.map((day) => {
                            const stat = weeklyHeaderStats[day.fullDate] || {
                              total: 0,
                              am: 0,
                              pm: 0,
                            };

                            return (
                              <th
                                key={day.fullDate}
                                className="py-2 px-4 text-sm border-b border-x border-gray-200 text-center"
                              >
                                <div className="flex flex-col items-center text-[11px] leading-tight">
                                  <span className="text-gray-500">
                                    {day.label}
                                  </span>
                                  <span className="font-semibold text-blue-600">
                                    {day.date}
                                  </span>

                                  <div className="w-full flex items-center justify-between gap-2 text-[9px] mt-[2px]">
                                    <div>
                                      <span className="text-[10px] text-gray-500 mt-1">
                                        {stat.total} Appts
                                      </span>
                                    </div>
                                    <div className="flex gap-2">
                                      <span className="text-red-500">
                                        AM: {stat.am}
                                      </span>
                                      <span className="text-green-600">
                                        PM: {stat.pm}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </th>
                            );
                          })
                        ) : (
                          /* PROVIDER HEADER */
                        <>
  {allProviders.map((provider) => (
    <th
      key={provider}
      className="py-2 px-4 text-sm border-b border-x border-gray-200 text-[#2A2A2A] font-medium text-center"
    >
      <div className="flex justify-center items-center text-[11px] whitespace-nowrap">
        <img
          src="https://tse4.mm.bing.net/th?id=OIP.yOX1_KBsR6fClg3ek8G_ZwHaHx&pid=Api&P=0&h=180"
          alt="Profile"
          className="w-6 h-6 mr-2 rounded-full border border-gray-300"
        />
        {provider}
      </div>
    </th>
  ))}
</>

                        )}
                      </tr>
                    </thead>

                    <tbody className="overflow-y-auto">
                      {timeSlots.map((timeSlot) => (
                        <tr key={timeSlot}>
                          <td
                            hidden={isLoading}
                            className="border-b px-4 text-xs border-gray-200 w-[60px] font-inter whitespace-nowrap"
                          >
                            {timeSlot}
                          </td>

                          {/* WEEKLY BODY */}
                          {viewType === "weekly" ? 
                          (
                            weekDays.map((day) => {
                              const dayAppointments =
                                weeklyGroupedAppointments[timeSlot]?.[
                                  day.fullDate
                                ] || [];
                              const cellKey = `${timeSlot}-${day.fullDate}`;
                              const isExpanded = expandedRows[cellKey];

                              return (
                                <td
                                  key={day.fullDate}
                                  className="border-b border-x border-gray-200 px-1 align-top"
                                >
                                  {dayAppointments
                                    .slice(
                                      0,
                                      isExpanded
                                        ? dayAppointments.length
                                        : 3
                                    )
                                    .map((appointment) => (
                                      <div
                                        key={appointment.appointmentid}
                                        onClick={() => {
                                          setSelectedAppointment(appointment);
                                          setIsAptCard(true);
                                        }}
                                        className="bg-[#ECECEE] w-[186px] cursor-pointer flex items-center text-sm py-[1px] h-auto justify-between rounded-lg mb-2"
                                      >
                                        <span
                                          className="h-[18px] px-[2px]"
                                          style={{
                                            backgroundColor:
                                              getAppointmentColorClass(
                                                appointment.type
                                              ),
                                            borderBottomLeftRadius: "10px",
                                            borderTopLeftRadius: "10px",
                                          }}
                                        />
                                        <div className="flex items-center w-full ml-1">
                                          <span className="text-[11px] truncate">
                                            {appointment.firstname}{" "}
                                            {appointment.lastname}
                                          </span>
                                        </div>
                                        <div className="ml-auto text-[10px] mr-1">
                                          {appointment.patientid}
                                        </div>
                                      </div>
                                    ))}

                                  {dayAppointments.length > 3 && (
                                    <div
                                      className="text-[10px] text-center text-gray-500 cursor-pointer"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                 const eventDate = dayAppointments[0]?.startdate;

if (eventDate) {
  const formattedDate = dayjs(eventDate).format("YYYY-MM-DD");
  openDailyFromMonthly(formattedDate);
}

                                      }}
                                    >
                                      {isExpanded ? "View Less" : "View More"}
                                    </div>
                                  )}
                                </td>
                              );
                            })
                          ) :
                          <>
  {allProviders.map((provider) => {
    const providerAppointments =
      groupedAppointments[timeSlot]?.[provider] || [];

    return (
      <td
        key={provider}
        className="border-b border-x border-gray-200 align-top min-h-[40px]"
      >
        {providerAppointments.length > 0 ? (
          <>
            {providerAppointments
              .slice(
                0,
                expandedRows[timeSlot]
                  ? providerAppointments.length
                  : 3
              )
              .map((appointment) => (
                <div
                  key={appointment.appointmentid}
                  onClick={() => {
                    setSelectedAppointment(appointment);
                    setIsAptCard(true);
                  }}
                  className="bg-[#ECECEE] w-[186px] cursor-pointer flex items-center text-sm py-[1px] rounded-lg mb-2"
                >
                  <span
                    className="h-[18px] px-[2px]"
                    style={{
                      backgroundColor: getAppointmentColorClass(
                        appointment.type
                      ),
                      borderBottomLeftRadius: "10px",
                      borderTopLeftRadius: "10px",
                    }}
                  />
                  <span className="ml-1 text-[11px] truncate">
                    {appointment.firstname} {appointment.lastname}
                  </span>
                </div>
              ))}

            {providerAppointments.length > 3 && (
              <div
                className="text-gray-500 text-[10px] cursor-pointer text-center"
                onClick={() => handleExpandToggle(timeSlot)}
              >
                {expandedRows[timeSlot] ? "View Less" : "View More"}
              </div>
            )}
          </>
        ) : (
          // 👇 THIS is what keeps borders visible
          <div className="h-8 w-full">&nbsp;</div>
        )}
      </td>
    );
  })}
</>

                          }
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {viewType === "monthly" && (
                  <div className="grid grid-cols-7 gap-px bg-gray-200 rounded-lg overflow-hidden">
                    {/* Header */}
                    {weekLabels.map((day) => (
                      <div
                        key={day}
                        className="bg-white text-center py-2 text-xs font-semibold text-gray-600"
                      >
                        {day}
                      </div>
                    ))}

                    {/* Days */}
                    {monthDays.map((date) => {
                      const dayKey = date.format("YYYY-MM-DD");
                      const appointments =
                        monthlyGroupedAppointments[dayKey] || [];
                      const isExpanded = expandedRows[dayKey];

                      const amCount = appointments.filter(
                        (a) => parseInt(a.starttime.split(":")[0], 10) < 12
                      ).length;

                      const pmCount = appointments.length - amCount;

                      return (
                        <div
                          key={dayKey}
                          className={`bg-white min-h-[120px] p-2 border
                            ${
                              date.month() !== selectedDate.month()
                                ? "bg-gray-50 text-gray-400"
                                : ""
                            }
                          `}
                        >
                          {/* Date */}
                          <div className="flex items-center justify-between">
                            <div className="text-xs text-start font-semibold">
                              {date.date()}
                            </div>
                            {appointments.length > 0 && (
                              <div className="flex items-center gap-1 text-[10px] mt-1">
                                <span className="text-red-500">
                                  AM: {amCount}
                                </span>
                                <span className="text-gray-400 mx-[2px]">
                                  |
                                </span>
                                <span className="text-green-600">
                                  PM: {pmCount}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Appointments */}
                          <div className="mt-1 space-y-[2px]">
                            {appointments
                              .slice(
                                0,
                                isExpanded ? appointments.length : 3
                              )
                              .map((appt) => (
                                <div
                                  key={appt.appointmentid}
                                  onClick={() => {
                                    setSelectedAppointment(appt);
                                    setIsAptCard(true);
                                  }}
                                  className="bg-[#ECECEE] w-[186px] cursor-pointer flex items-center text-sm py-[1px] h-auto justify-between rounded-lg mb-2"
                                >
                                  <span
                                    className="h-[18px] px-[2px]"
                                    style={{
                                      backgroundColor:
                                        getAppointmentColorClass(appt.type),
                                      borderBottomLeftRadius: "10px",
                                      borderTopLeftRadius: "10px",
                                    }}
                                  />
                                  <div className="flex items-center w-full ml-1">
                                    <span className="text-[11px] truncate">
                                      {appt.firstname} {appt.lastname}
                                    </span>
                                  </div>
                                  <div className="ml-auto text-[10px] mr-1">
                                    {appt.patientid}
                                  </div>
                                </div>
                              ))}

                            {appointments.length > 3 && (
                              <div
                                className="text-[10px] text-blue-600 cursor-pointer"
                                onClick={(e) => {
                                  e.stopPropagation();
                             const eventDate = appointments[0]?.startdate;
console.log("eventDate", eventDate);
if (eventDate) {
  const formattedDate = dayjs(eventDate).format("YYYY-MM-DD");
  openDailyFromMonthly(formattedDate);
}

                                }}
                              >
                                {isExpanded
                                  ? "View Less"
                                  : `+ ${appointments.length - 3} more`}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </SideNavLayout>
        <Backdrop
          sx={{
            color: "secondary.main",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={isLoading}
        >
          <CircularProgress color="secondary" />
        </Backdrop>
      </div>

      <NewAppointments
        fetchScheduleData={fetchScheduleData}
        selectedAppointment={selectedAppointment}
        Appointments={Appointments}
        setNewAppointments={setNewAppointments}
      />
      <ModalSwitch
        isfutureappointment={isfutureappointment}
        setIsFutureAppointment={setIsFutureAppointment}
        setScreen={setScreen}
        screen={screen}
        selectedDate={selectedDate}

        fetchScheduleData={fetchScheduleData}

        appointmentCardopen={appointmentCardopen}
        setIsappointmentCardOpen={setIsappointmentCardOpen}
        isAptcard={isAptCard}
        setIsAptcard={setIsAptCard}
        selectedAppointment={selectedAppointment}
      />
    </>
  );
};

export default Schedule;